// @ts-nocheck -- Legacy JavaScript compatibility boundary; runtime behavior intentionally preserved.
/* eslint-disable -- Legacy JavaScript compatibility boundary; runtime behavior intentionally preserved. */
import * as yup from "yup";
import { useEffect, useState } from "react";
import Image from "next/image";
import useTranslation from "next-translate/useTranslation";
import { useMediaQuery, useTheme, Box, CircularProgress } from "@mui/material";

import { useStepperContext } from "@/hooks/useStepperContext";
import { useCountdownTimer } from "@/hooks";
import { Button, Form, Grid, Typography } from "@/components/core";
import { useUserInfoStore } from "@/store/login/user-info";
import { useCheckOtpStore } from "@/store/login/check-otp";
import { useSendOtpStoreStore } from "@/store/login/send-otp";
import { useSecurityImageStore } from "@/store/login/security-image";
import { MessageDialog } from "@/components/common";

export function PhoneConfirm() {
  const stepper = useStepperContext();
  const theme = useTheme();
  const { t } = useTranslation("login");
  const mobileMediaQuery = theme.breakpoints.down("md");
  const isMobileDevice = useMediaQuery(mobileMediaQuery);

  const [completed, setCompleted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true); // ✅ Sayfa yükleniyor durumu

  const userInfo = useUserInfoStore((state: any) => state.data);
  const { checkOtp, error } = useCheckOtpStore((state: any) => state);
  const { sendOtp } = useSendOtpStoreStore((state: any) => state);
  const { securityImage, getSecurityUserImage } = useSecurityImageStore();

  useEffect(() => {
    async function fetchSecurityImage() {
      if (userInfo.systemUserId) {
        await getSecurityUserImage(userInfo.systemUserId);
      }

      setIsPageLoading(false);
    }

    fetchSecurityImage();
  }, []);

  const {
    timeLeft: { minutes, seconds },
    isCountdownOver,
    resetCountdown,
  } = useCountdownTimer({ countdownSeconds: 90 });

  const formProps = {
    initialValues: {
      phoneConfirm: "",
    },
    validationSchema: yup.object().shape({
      phoneConfirm: yup.string().length(6, t("confirmCodeError")).required(t("confirmCodeError")),
    }),
    onSubmit: async (data: any) => {
      setLoading(true);
      await checkOtp({ ...data, ...userInfo }, stepper);

      if (error !== null && error.body.type === "NexusBusinessExceptionDetail") {
        setErrorMessage(error.body.message);
        setMessageDialogOpen(true);
      }

      setLoading(false);
    },
  };

  const handleMessageDialogClose = () => {
    setMessageDialogOpen(false);
  };

  return (
    <>
      {isPageLoading ? (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%", minHeight: 200 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Form {...formProps}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "flex-start",
              mt: { xs: 2, md: 4 },
            }}
          >
            <Grid
              sx={{
                width: "320px",
                mx: "auto",
                py: { xs: 4, md: 0 },
                "& .MuiGrid-item": { paddingX: 0 },
              }}
              spacing={2}
              direction="column"
            >
              <Grid.Item>
                {securityImage ? (
                  <Grid.Item sx={{ textAlign: "center", mb: 3 }}>
                    <Typography id="selectedSecurityImageLabel" fontSize={15} textAlign="center">
                      {t("selectedSecurityImages")}
                    </Typography>
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                      <Image
                        src={securityImage.path}
                        alt="Güvenlik Resmi"
                        width={80}
                        height={80}
                        objectFit="contain"
                      />
                    </Box>
                  </Grid.Item>
                ) : (
                  <Grid.Item sx={{ textAlign: "center" }}>
                    <Typography id="selectedSecurityImageLabel" fontSize={12} textAlign="center">
                      {t("otpSecurityImagesNot")}
                    </Typography>
                  </Grid.Item>
                )}
                <Typography id="confirm" fontSize={20} textAlign="center" bold>
                  {t("smsVerification")}
                </Typography>
                <Typography id="sentConfirmCode" textAlign="center" marginTop="0.3rem">
                  {t("sentConfirmCode")}
                </Typography>
              </Grid.Item>
              <Grid.Item>
                <Typography id="userPhone" fontSize={18} textAlign="center" bold>
                  {userInfo.phoneNumber}
                </Typography>
              </Grid.Item>
              <Grid.Item>
                <Form.CodeVerification
                  variant="filled"
                  length={6}
                  name="phoneConfirm"
                  aria-labelledby="phoneConfirmLabel"
                  onComplete={() => setCompleted(true)}
                  helperText={{
                    fontSize: theme.codeVerification.helperText.fontSize,
                    color: theme.codeVerification.helperText.color,
                    textAlign: "center",
                  }}
                />
              </Grid.Item>
              <Grid.Item>
                {isCountdownOver && (
                  <Typography id="countdownOver" marginBottom="0.1rem" color="red">
                    {t("countdownOver")}
                  </Typography>
                )}
                <Button
                  onClick={async () => {
                    await sendOtp(userInfo);
                    resetCountdown();
                  }}
                  disabled={!isCountdownOver}
                  color="inherit"
                  sx={{ fontSize: 16, fontWeight: "bold", width: "100%" }}
                  aria-label="Send OTP again"
                >
                  {t("sendAgain")}
                </Button>
                <Typography textAlign="center" marginTop="0.2rem">
                  {`${minutes}:${seconds} ${t("minutes")}`}
                </Typography>
              </Grid.Item>
              <Grid.Item sx={{ width: "100%" }}>
                <Button
                  sx={{
                    fontSize: 18,
                    padding: 2,
                    borderRadius: 2,
                    width: "100%",
                  }}
                  variant="contained"
                  type="submit"
                  disabled={!completed || loading}
                  aria-label="Submit OTP"
                >
                  {t("common:nextButton")}
                </Button>
              </Grid.Item>
            </Grid>
          </Box>
        </Form>
      )}
      {errorMessage && (
        <MessageDialog
          message={errorMessage}
          open={messageDialogOpen}
          onClose={handleMessageDialogClose}
        />
      )}
    </>
  );
}
